import type { DeviceBindingCreate } from './deviceBinding';
import type { PairingInput, PairingMethod } from './pairingQr';

export type PairingProvisioningMethod = PairingMethod;
export type PairingProvisioningDeliveryState = 'direct' | 'native-provider-required';
export type PairingProvisioningCandidateState = PairingProvisioningDeliveryState | 'unavailable';
export type PairingProvisioningAvailabilityIssue =
  | 'bluetooth-unavailable'
  | 'wifi-credentials-missing'
  | 'soft-ap-ssid-missing'
  | 'soft-ap-join-unavailable'
  | 'local-network-unavailable'
  | 'native-transport-unavailable';

export interface PairingProvisioningCapabilitySnapshot {
  bluetoothAvailable?: boolean;
  wifiCredentialsAvailable?: boolean;
  softApJoinAvailable?: boolean;
  localNetworkAvailable?: boolean;
  nativeTransportAvailable?: boolean;
}

export interface PairingProvisioningCandidate {
  method: PairingProvisioningMethod;
  deliveryState: PairingProvisioningCandidateState;
  issues: PairingProvisioningAvailabilityIssue[];
  fallback: boolean;
}

export interface PairingProvisioningHandoff {
  bindingId: string;
  ownerToken: string;
  serverUrl: string;
  region?: string;
  deviceId?: string;
  model?: string;
  methods: PairingProvisioningMethod[];
  preferred: PairingProvisioningMethod;
  deliveryState: PairingProvisioningDeliveryState;
  fallback: PairingProvisioningMethod | null;
  candidates: PairingProvisioningCandidate[];
  softApSsid?: string;
  softApPassphrase?: string;
  // Device timezone POSIX string seeded at bind (common/22 §3). The device writes
  // it to config.cfg and self-reports it at the post-bind sign-in (empty-only
  // cloud backfill). Pre-selected from the browser/OS zone; omitted when unknown.
  // NOT the IANA WeeklySchedule timezone (ADR-054).
  tz?: string;
}

export function preferredPairingProvisioningMethod(methods: PairingProvisioningMethod[]): PairingProvisioningMethod {
  if (methods.includes('ble')) return 'ble';
  return methods[0] ?? 'ble';
}

export function pairingProvisioningMethods(input: PairingInput): PairingProvisioningMethod[] {
  return input.payload.methods;
}

export function selectPairingProvisioningMethods(
  methods: PairingProvisioningMethod[],
  selectedMethod?: PairingProvisioningMethod | null
): PairingProvisioningMethod[] {
  if (selectedMethod && methods.includes(selectedMethod)) return [selectedMethod];
  return methods;
}

export function pairingProvisioningDeliveryState(
  _method: PairingProvisioningMethod
): PairingProvisioningDeliveryState {
  return 'native-provider-required';
}

function pairingProvisioningCandidateIssues(
  method: PairingProvisioningMethod,
  input: PairingInput,
  capabilities: PairingProvisioningCapabilitySnapshot
): PairingProvisioningAvailabilityIssue[] {
  const qr = input.payload;
  const issues: PairingProvisioningAvailabilityIssue[] = [];
  if (method === 'ble') {
    if (!capabilities.bluetoothAvailable) issues.push('bluetooth-unavailable');
    if (!capabilities.wifiCredentialsAvailable) issues.push('wifi-credentials-missing');
    if (!capabilities.nativeTransportAvailable) issues.push('native-transport-unavailable');
  } else if (method === 'ap') {
    if (!qr.softApSsid) issues.push('soft-ap-ssid-missing');
    if (!capabilities.softApJoinAvailable) issues.push('soft-ap-join-unavailable');
    if (!capabilities.localNetworkAvailable) issues.push('local-network-unavailable');
    if (!capabilities.wifiCredentialsAvailable) issues.push('wifi-credentials-missing');
    if (!capabilities.nativeTransportAvailable) issues.push('native-transport-unavailable');
  }
  return issues;
}

function pairingProvisioningCandidateState(
  issues: PairingProvisioningAvailabilityIssue[]
): PairingProvisioningCandidateState {
  if (issues.length > 0) return 'unavailable';
  return 'native-provider-required';
}

export function pairingProvisioningCandidates(
  input: PairingInput,
  methods: PairingProvisioningMethod[],
  capabilities: PairingProvisioningCapabilitySnapshot = {}
): PairingProvisioningCandidate[] {
  return methods.map((method) => {
    const issues = pairingProvisioningCandidateIssues(method, input, capabilities);
    return {
      method,
      deliveryState: pairingProvisioningCandidateState(issues),
      issues,
      fallback: false,
    };
  });
}

export function buildPairingProvisioningHandoff(
  input: PairingInput,
  binding: DeviceBindingCreate,
  selectedMethod?: PairingProvisioningMethod | null,
  capabilities: PairingProvisioningCapabilitySnapshot = {},
  tz?: string // device timezone POSIX seed (common/22 §3); caller passes browserPosixDefault()
): PairingProvisioningHandoff {
  const { payload } = input;
  const availableMethods = pairingProvisioningMethods(input);
  const preferred = selectedMethod && availableMethods.includes(selectedMethod)
    ? selectedMethod
    : preferredPairingProvisioningMethod(availableMethods);
  const methods = selectPairingProvisioningMethods(availableMethods, selectedMethod);
  return {
    bindingId: binding.binding_id,
    ownerToken: binding.owner_token,
    serverUrl: binding.server_url,
    region: binding.region ?? payload.region,
    deviceId: payload.deviceId,
    model: payload.model,
    methods,
    preferred,
    deliveryState: pairingProvisioningDeliveryState(preferred),
    fallback: null,
    candidates: pairingProvisioningCandidates(input, methods, capabilities),
    softApSsid: payload.softApSsid,
    softApPassphrase: payload.softApPassphrase,
    ...(tz ? { tz } : {}), // common/22 §3: seed device tz (POSIX) when the caller supplies it
  };
}
